import { useCallback, useRef } from "react";

import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import { ClientApplicationFragment, useUpdateClientApplicationMutation, ClientApplicationForTableFragment } from "@graphql/client-applications/ClientApplications.g";

import { SlideInPanel } from "@design-system/SlideInPanel/SlideInPanel";

import { ClientApplicationApprovalReviewForm, ClientApplicationApprovalReviewFormValues } from "@components/Forms/ClientApplicationApprovalReviewForm/ClientApplicationApprovalReviewForm";

type Props = {
  application: ClientApplicationFragment | ClientApplicationForTableFragment | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const ClientApplicationReviewPanel = ({
  application,
  clientId,
  isOpen,
  onClose,
  onSuccess,
}: Props) => {
  const updateClientApplicationMutation = useUpdateClientApplicationMutation();
  const formRef = useRef<HTMLFormElement>(null);

  const handleFormSubmit = useCallback(
    async (values: ClientApplicationApprovalReviewFormValues) => {
      if (!application?.id) return;
      
      try {
        await updateClientApplicationMutation.mutateAsync({
          input: {
            id: application.id,
            clientId: values.clientId,
            reviewState: values.reviewState,
            managedBy: values.managedBy,
            supportedBy: values.supportedBy,
            businessOwnerId: values.businessOwnerId,
            notes: values.notes,
          },
        });
        onClose();
        onSuccess?.();
      } catch (error) {
        console.error("Failed to update client application:", error);
      }
    },
    [application, updateClientApplicationMutation, onClose, onSuccess],
  );

  const handleApply = useCallback(() => {
    // Trigger form submission by dispatching a submit event
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  if (!application) {
    return null;
  }

  return (
    <SlideInPanel
      isOpen={isOpen}
      onClose={onClose}
      title="Application Review"
      onApply={handleApply}
      applyButtonText="Update Application"
    >
      <ClientApplicationApprovalReviewForm
        ref={formRef}
        values={{
          reviewState: application.reviewState ?? TenantedClientApplicationReviewStateChoices.Discovered,
          managedBy: application.managedBy ?? undefined,
          supportedBy: application.supportedBy ?? undefined,
          businessOwnerId: application.businessOwner?.id ?? undefined,
          clientId: clientId,
          notes: application.notes,
        }}
        onSubmit={handleFormSubmit}
        withSubmitButton={false}
      />
    </SlideInPanel>
  );
};